import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { findThingInRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { WidgetType , HubServiceState, HubServiceSubscription } from '@kemu-io/kemu-core/types';
import { LogicMapperState } from '../logicMapperSlice';
import * as recipeUtils from '@src/app/recipe/utils';
import { StatelessWidgetsMap, WidgetsMap } from '@src/types/core_t';
import { widgetMapToStatelessMap } from '@common/utils';
import connectionManager from '@src/app/kemuHub/connectionManager';

type RemoveWidgetsPayload = {
  /** the id of the recipe in the pool */
  recipeId: string;
  /** the id of the Thing in the recipe the widgets will be removed from */
  thingId: string;
  /** the list of widgets ids in the Thing to be removed */
  widgetsIds: string[];
  // /** if provided, `widgetsIds` is ignored and instead, all the currently selected widgets are removed. */
  // allSelected?: boolean;
  clearSelection?: boolean;
  /**
   * Widget IDs (format: thingId:widgetId) that existed when entering execution mode.
   * If provided, widgets in this list will not have their terminate function called
   * since they will be restored when exiting execution mode.
   */
  editingModeWidgetIds?: string[] | null;
}

/**
 * Removes a list of widgets from the recipe.
 */
export const removeWidgetsAction = createAsyncThunk(
  'LogicMapper/removeWidgets',  async (
    payload: RemoveWidgetsPayload,
): Promise<{
  statelessMap: StatelessWidgetsMap;
  clearSelection?: boolean;
}> => {

  let remainingWidgetsInThing: WidgetsMap = {};
  const thing = findThingInRecipe(payload.recipeId, payload.thingId);
  if (!thing) {
    throw new Error(`Thing [${payload.thingId}] not found in recipe [${payload.recipeId}]`);
  }

  // Will keep track of services to unsubscribe from
  // const hubSubscriptionsToRemove: HubServiceSubscription['targetService'] [] = [];
  const subscriptionRemovalMap: Record<string, HubServiceSubscription['targetService']> = {};

  // Build a set of editing mode widget IDs for efficient lookup
  const editingModeWidgetIdsSet = payload.editingModeWidgetIds 
    ? new Set(payload.editingModeWidgetIds) 
    : null;

  // const widgetsInThing = Object.keys(thing.gates);
  for (const widgetId of payload.widgetsIds) {
    // const widget = findWidgetInRecipe(payload.recipeId, payload.thingId, widgetId);
    const widget = thing.gates[widgetId];
    // If a widget bundle, make sure to remove is /tmp location in cache.

    // Keep track of which subscriptions need to be removed.
    if (widget.type === WidgetType.hubService) {
      const hubServiceState = widget.state as HubServiceState;
      if (hubServiceState.service?.eventEmitter) {
        const key = `${hubServiceState.service.name}_${hubServiceState.service.version}`;
        if (!subscriptionRemovalMap[key]) {
          subscriptionRemovalMap[key] = {
            serviceName: hubServiceState.service.name,
            version: hubServiceState.service.version
          };
        }
      }
    }

    // Check if this widget existed in edit mode - if so, skip termination
    // because it will be restored when exiting execution mode
    const compositeWidgetId = `${payload.thingId}:${widgetId}`;
    const shouldSkipTerminate = editingModeWidgetIdsSet?.has(compositeWidgetId) ?? false;

    // Finally remove the actual widget reference from the recipe
    remainingWidgetsInThing = await recipeUtils.removeWidget(
      payload.recipeId,
      payload.thingId,
      widgetId,
      { skipTerminate: shouldSkipTerminate }
    );
  }

  // Check if there is any remaining widget also subscribed to any services to be removed
  for (const widgetId of Object.keys(remainingWidgetsInThing)) {
    const widget = remainingWidgetsInThing[widgetId];
    if (widget.type === WidgetType.hubService) {
      const hubServiceState = widget.state as HubServiceState;
      if (hubServiceState.service?.eventEmitter) {
        const key = `${hubServiceState.service.name}_${hubServiceState.service.version}`;
        if (subscriptionRemovalMap[key]) {
          delete subscriptionRemovalMap[key];
        }
      }
    }
  }

  // Unsubscribe from services
  const subscriptionsToRemove = Object.values(subscriptionRemovalMap);
  if (subscriptionsToRemove.length > 0) {
    const hc = connectionManager.getConnector();
    for (const subscription of subscriptionsToRemove) {
      hc.unsubscribeFromServiceEvents({
        targetService: subscription,
        listener: {
          recipeId: payload.recipeId
        }
      });
    }
  }


  const statelessMap = widgetMapToStatelessMap(remainingWidgetsInThing);
  return {
    statelessMap,
    clearSelection: payload.clearSelection
  };
});

export const removeWidgetsReducer = ((builder: ActionReducerMapBuilder<LogicMapperState>): void => {
  builder.addCase(removeWidgetsAction.fulfilled, (state, action) => {
    if (action.payload.clearSelection) {
      state.selectedWidgets = [];
    }

    state.gates = action.payload.statelessMap;
  });

  builder.addCase(removeWidgetsAction.rejected, (_draft, action) => {
    console.error('Failed to remove widget : ', action.error);
  });
});
